import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CBadge, CButton, CCol, CFormInput, CRow } from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";

import TNTable from "@/components/TNTable";
import CustomDropdown from "@/components/CustomDropdown";
import { useTranslationContext } from "@/context/TranslationContext";
import { subscriptionPlan } from "@/constants/translation/subscriptionPlan.translation";
import {
  breadcrumb_key,
  messages,
} from "@/constants/translation/common.translation";
import { masters } from "@/libs/routes";
import constants from "@/libs/constant";
import { setFormatDate } from "@/utils/dateFormate";
import ReadMore from "@/components/ReadMore";
import {
  useSubscriptionPlanList,
  useDeleteSubscriptionPlan,
  useUpdateSubscriptionPlanStatus,
} from "@/hooks/useSubscriptionPlan";

const STATUS_FILTER_VALUES = ["all", "active", "inactive"];
const USER_TYPE_FILTER_VALUES = ["all", "normal_user", "freelancer_user"];

// API responses pass every value through the app's shared encryption round-trip,
// which stringifies booleans (true -> "true", false -> "false"). Native JS truthiness
// on the raw value is wrong here since the string "false" is truthy.
const isActiveValue = (value) =>
  value === true || value === constants.status.apiStatus.true;

const getUserTypeBadgeClass = (userType) => {
  switch (userType) {
    case "freelancer_user":
      return "primary";
    case "normal_user":
      return "secondary";
    default:
      return "secondary";
  }
};

const ListSubscriptionPlanPage = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();

  const canAdd = true;
  const canUpdate = true;
  const canDelete = true;

  const [currentPage, setCurrentPage] = useState(
    constants.pagination.defaultPage,
  );
  const [searchText, setSearchText] = useState("");
  const [tempSearchText, setTempSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [userTypeFilter, setUserTypeFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState(constants.sort.order.desc);
  const searchTimeout = useRef(null);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const handleSearch = (e) => {
    const value = e.target.value;
    setTempSearchText(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      setSearchText(value);
      setCurrentPage(1);
    }, 400);
  };

  const handleClearFilter = () => {
    setTempSearchText("");
    setSearchText("");
    setStatusFilter("all");
    setUserTypeFilter("all");
    setCurrentPage(constants.pagination.defaultPage);
  };

  const {
    data: listData,
    isLoading,
    refetch,
  } = useSubscriptionPlanList({
    page: currentPage || constants.pagination.defaultPage,
    limit: constants.pagination.defaultPageSize,
    ...(searchText && searchText.trim() ? { search: searchText.trim() } : {}),
    ...(statusFilter && statusFilter !== "all" ? { status: statusFilter } : {}),
    ...(userTypeFilter && userTypeFilter !== "all"
      ? { user_type: userTypeFilter }
      : {}),
    sort_by: sortField,
    order_by: sortOrder,
  });

  useEffect(() => {
    refetch();
  }, [refetch]);

  const [subscriptionPlanList, setSubscriptionPlanList] = useState([]);
  const [paginationData, setPaginationData] = useState(null);

  useEffect(() => {
    if (listData && !isLoading) {
      setSubscriptionPlanList(listData.data?.subscriptionPlanList || []);
      setPaginationData(listData.data?.pagination);
    }
  }, [listData, isLoading]);

  const { mutate: doDeleteSubscriptionPlan } = useDeleteSubscriptionPlan(
    (response) => {
      toast.success(
        response?.message || t(messages.toast.subscriptionPlan.deleted),
      );
      refetch();
    },
    (error) => {
      toast.error(
        error?.response?.data?.message ||
          t(messages.toast.subscriptionPlan.deleteFailed),
      );
    },
  );

  const { mutate: doUpdateStatus } = useUpdateSubscriptionPlanStatus(
    (response) => {
      toast.success(
        response?.message || t(messages.toast.subscriptionPlan.statusUpdated),
      );
      refetch();
    },
    (error) => {
      toast.error(
        error?.response?.data?.message ||
          t(messages.toast.subscriptionPlan.statusUpdateFailed),
      );
    },
  );

  const handleEditClick = (row) => {
    navigate(masters.subscriptionPlan.edit.replace(":id", row.uuid));
  };

  const handleDeleteClick = (row) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon
              className="alert-close"
              icon={faClose}
              onClick={onClose}
            />
            <h2 className="alert-title">
              {t(subscriptionPlan.list.delete_popup_heading_text)}
            </h2>
            <p className="text-center mb-3">
              {t(subscriptionPlan.list.delete_popup_message_text)}
            </p>
            <div className="d-flex gap-2 justify-content-center">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doDeleteSubscriptionPlan(row.uuid);
                  onClose();
                }}
              >
                {t(subscriptionPlan.list.delete_popup_yes_btn_text)}
              </CButton>
              <CButton
                className="table-primary-button"
                color="danger"
                onClick={onClose}
              >
                {t(subscriptionPlan.list.delete_popup_no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleStatusClick = (row) => {
    const nextStatus = !isActiveValue(row.isActive);

    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon
              className="alert-close"
              icon={faClose}
              onClick={onClose}
            />
            <h2 className="alert-title">
              {t(subscriptionPlan.list.status_popup_heading_text)}
            </h2>
            <div className="d-flex gap-2 justify-content-center">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doUpdateStatus({ uuid: row.uuid, isActive: nextStatus });
                  onClose();
                }}
              >
                {t(subscriptionPlan.list.status_popup_yes_btn_text)}
              </CButton>
              <CButton
                className="table-primary-button"
                color="danger"
                onClick={onClose}
              >
                {t(subscriptionPlan.list.status_popup_no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const statusFilterOptions = useMemo(
    () =>
      STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: t(subscriptionPlan.list.status_filter_options[value]),
      })),
    [t],
  );

  const userTypeFilterOptions = useMemo(
    () =>
      USER_TYPE_FILTER_VALUES.map((value) => ({
        value,
        label: t(subscriptionPlan.list.user_type_filter_options[value]),
      })),
    [t],
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(
        sortOrder === constants.sort.order.asc
          ? constants.sort.order.desc
          : constants.sort.order.asc,
      );
    } else {
      setSortField(field);
      setSortOrder(constants.sort.order.asc);
    }
    setCurrentPage(1);
  };

  const sortableHeader = (label, field) => (
    <button
      type="button"
      className="table-sort-button"
      onClick={(event) => {
        event.stopPropagation();
        handleSort(field);
      }}
    >
      {label}
    </button>
  );

  const columns = useMemo(
    () => [
      {
        id: "createdAt",
        accessorKey: "createdAt",
        sortField: "created_at",
        header: sortableHeader(
          t(subscriptionPlan.list.table.created_at_col_heading),
          "created_at",
        ),
        enableSorting: true,
        cell: ({ getValue }) => <span>{setFormatDate(getValue())}</span>,
      },
      {
        id: "name",
        accessorKey: "name",
        sortField: "name",
        header: sortableHeader(
          t(subscriptionPlan.list.table.name_col_heading),
          "name",
        ),
        enableSorting: true,
        cell: ({ row }) => (
          <ReadMore maxLength={30}>{row.original.name}</ReadMore>
        ),
      },
      {
        id: "userType",
        accessorKey: "userType",
        sortField: "user_type",
        header: sortableHeader(
          t(subscriptionPlan.list.table.user_type_col_heading),
          "user_type",
        ),
        enableSorting: true,
        cell: ({ getValue }) => (
          <CBadge color={getUserTypeBadgeClass(getValue())}>
            {t(subscriptionPlan.user_type[getValue()]) || getValue()}
          </CBadge>
        ),
      },
      {
        id: "productIds",
        accessorKey: "appleProductId",
        sortField: "product_id",
        header: sortableHeader(
          t(subscriptionPlan.list.table.product_ids_col_heading),
          "product_id",
        ),
        enableSorting: true,
        cell: ({ row }) => {
          const { appleProductId, googleProductId } = row.original;
          return (
            <div>
              {appleProductId && (
                <div>
                  {t(subscriptionPlan.list.apple_product_id_prefix)}{" "}
                  {appleProductId}
                </div>
              )}
              {googleProductId && (
                <div>
                  {t(subscriptionPlan.list.google_product_id_prefix)}{" "}
                  {googleProductId}
                </div>
              )}
            </div>
          );
        },
      },
      {
        id: "isActive",
        accessorKey: "isActive",
        sortField: "status",
        header: sortableHeader(
          t(subscriptionPlan.list.table.status_col_heading),
          "status",
        ),
        enableSorting: true,
        cell: ({ row }) => {
          const isActive = isActiveValue(row.original.isActive);
          return (
            <span
              className={`cursor-pointer ${isActive ? "text-success" : "text-danger"}`}
              onClick={() => canUpdate && handleStatusClick(row.original)}
            >
              {isActive
                ? t(subscriptionPlan.list.status.active)
                : t(subscriptionPlan.list.status.inactive)}
            </span>
          );
        },
      },
      {
        id: "actions",
        accessorKey: "uuid",
        header: t(subscriptionPlan.list.table.action_col_heading),
        cell: ({ row }) => (
          <div className="d-flex gap-2 justify-content-center">
            {canUpdate && (
              <div
                className="table-action-text"
                onClick={() => handleEditClick(row.original)}
              >
                <FontAwesomeIcon icon={faEdit} className="list-icon" />
              </div>
            )}
            {canDelete && (
              <div
                className="table-action-text"
                onClick={() => handleDeleteClick(row.original)}
              >
                <FontAwesomeIcon icon={faTrash} className="list-icon" />
              </div>
            )}
          </div>
        ),
      },
    ],
    [t, sortField, sortOrder],
  );

  return (
    <div className="subscription-plan-list">
      <CRow className="list-align">
        <CCol
          lg={10}
          md={10}
          sm={12}
          xs={12}
          className="d-flex gap-2 justify-content-end flex-wrap"
        >
          <CFormInput
            placeholder={t(subscriptionPlan.list.search_placeholder)}
            type="text"
            value={tempSearchText}
            onChange={handleSearch}
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "300px", height: "38px" }}
          />

          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "200px", height: "38px", padding: "0px" }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={statusFilterOptions}
          />

          <CustomDropdown
            className="filter-field-space form-control-sm"
            style={{ maxWidth: "200px", height: "38px", padding: "0px" }}
            value={userTypeFilter}
            onChange={(e) => {
              setUserTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={userTypeFilterOptions}
          />

          <CButton
            color="primary"
            type="button"
            className="filter-field-space"
            onClick={handleClearFilter}
            style={{
              height: "36px",
              display:
                tempSearchText.trim() ||
                statusFilter !== "all" ||
                userTypeFilter !== "all"
                  ? "inline-block"
                  : "none",
            }}
          >
            {t(subscriptionPlan.list.clear_filter_btn_text)}
          </CButton>

          {canAdd && (
            <CButton
              color="primary"
              type="button"
              className="filter-field-space"
              style={{ height: "38px" }}
              onClick={() => navigate(masters.subscriptionPlan.add)}
            >
              {t(subscriptionPlan.list.create_btn_text)}
            </CButton>
          )}
        </CCol>
      </CRow>

      <TNTable
        columns={columns}
        data={subscriptionPlanList}
        paginationData={paginationData}
        onSelectPage={(pageIndex) => {
          setCurrentPage(pageIndex + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
        pageIndexGet={currentPage - 1}
        isLoading={isLoading}
        t={t}
        responsive={false}
        sortField={sortField}
        sortOrder={sortOrder}
        className="subscription-plan-table"
      />
    </div>
  );
};

ListSubscriptionPlanPage.breadCrumbArray = [
  {
    name: breadcrumb_key.masters?.subscriptionPlan?.list,
    pathname: masters.subscriptionPlan.list,
    active: true,
  },
];

export default ListSubscriptionPlanPage;
